// ═══════════════════════════════════════════════════════════
// hooks/useVisitorTracking.js
// Comptage des visiteurs uniques par IP (hachée, jamais stockée
// en clair — RGPD), avec historique mensuel.
// Web uniquement : les apps natives Android/iOS ont déjà d'autres
// moyens de mesurer leur usage (tokens FCM, Play Console...).
// ═══════════════════════════════════════════════════════════

import { useEffect } from 'react'
import { Capacitor } from '@capacitor/core'
import { doc, getDoc, setDoc, increment, serverTimestamp } from 'firebase/firestore'
import { db } from '../firebase/config'

/**
 * Hache une chaîne de caractères en SHA-256 (hexadécimal),
 * via l'API native du navigateur (aucune dépendance).
 */
async function sha256Hex(text) {
    const data = new TextEncoder().encode(text)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}

async function getClientIP() {
    try {
        const response = await fetch('https://api.ipify.org?format=json')
        const data = await response.json()
        return data.ip
    } catch (error) {
        console.error('Erreur récupération IP:', error)
        return null
    }
}

async function trackVisitor() {
    const SOURCE = 'app' // 'site' (johnnyroadradio.html) vs 'app' (React web)

    try {
        const today = new Date()
        const todayKey = today.toISOString().split('T')[0] // ex: 2026-08-17
        const monthKey = todayKey.slice(0, 7)               // ex: 2026-08

        // Évite de recompter la même journée si l'app est rechargée
        // plusieurs fois (léger cache local, pas de dépendance réseau).
        const lastTrackedDay = localStorage.getItem('visitor_last_tracked_day_app')
        if (lastTrackedDay === todayKey) return

        const ip = await getClientIP()
        if (!ip) {
            console.warn('⚠️ Impossible de récupérer l\'IP, visite non comptabilisée')
            return
        }

        const hashedIp = await sha256Hex(ip)

        const visitorRef = doc(db, '100johnny', 'main', 'visitor_stats', hashedIp)
        const monthRef = doc(db, '100johnny', 'main', 'monthly_visitor_stats', monthKey)

        const visitorSnap = await getDoc(visitorRef)
        const visitorData = visitorSnap.exists() ? visitorSnap.data() : {}
        const isNewVisitorGlobal = !visitorSnap.exists()
        const isNewVisitorForSource = !(visitorData.sourcesSeen && visitorData.sourcesSeen[SOURCE])

        // Mise à jour du document du visiteur
        const updateData = {
            lastSeen: serverTimestamp(),
            lastSeenDay: todayKey,
            totalVisitDays: increment(1),
            visitDaysByMonth: {
                [monthKey]: increment(1)
            },
            sourcesSeen: {
                [SOURCE]: true
            }
        }
        if (isNewVisitorGlobal) {
            updateData.firstSeen = serverTimestamp()
            updateData.firstSeenDay = todayKey
        }
        await setDoc(visitorRef, updateData, { merge: true })

        // Mise à jour des statistiques mensuelles agrégées (dashboard admin)
        // — un total combiné (tous sites confondus) + un détail par source.
        const monthUpdateData = {
            totalVisits: increment(1),
            bySource: {
                [SOURCE]: {
                    totalVisits: increment(1)
                }
            }
        }
        if (isNewVisitorGlobal) monthUpdateData.uniqueVisitors = increment(1)
        if (isNewVisitorForSource) monthUpdateData.bySource[SOURCE].uniqueVisitors = increment(1)
        await setDoc(monthRef, monthUpdateData, { merge: true })

        localStorage.setItem('visitor_last_tracked_day_app', todayKey)
        console.log(isNewVisitorGlobal ? '✅ Nouveau visiteur comptabilisé' : '✅ Visite du jour comptabilisée')

    } catch (error) {
        console.error('❌ Erreur comptage visiteur:', error)
    }
}

/**
 * À appeler une seule fois dans App.jsx (top-level du composant).
 * Ne fait rien sur Android/iOS natif — actif uniquement sur le web.
 */
export function useVisitorTracking() {
    useEffect(() => {
        if (Capacitor.isNativePlatform()) return
        trackVisitor()
    }, [])
}